import React from 'react';
import { Platform } from 'react-native';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { RepeatOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { imageBorderStyles, Stack, Tooltip, Typography } from '@lyearn/ui';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import ListCard from '@/modules/content/components/ContentCard/ListCard';
import { listCardImageStyles } from '@/modules/content/components/ContentCard/ListCard/styles';
import RegisteredClassInfo from '@/modules/events/views/EventDetails/components/RegisteredClassInfo';
import SeatsLeft from '@/modules/events/views/EventDetails/components/SeatsLeft';
import useEventResponse from '@/modules/events/views/EventDetails/hooks/useEventResponse';
import { EventStatusEnum, RecurringTypeEnum } from '@/types/schema';

import { useAdaptEvent } from '../../config';
import { EventCardProps } from '../types';
import { imageContainerStyles } from './styles';

function EventListCard(props: EventCardProps) {
  const { t } = useTranslation();

  const { isDesktop, isMobile } = useScreen();
  const { className, isLastItem, isLastItemInList, imageProps } = props;
  const event = useAdaptEvent(props.event);
  const { isFillingFast } = props.event;
  const eventResponse = useEventResponse(props.event);
  const {
    isRegistrationRequired,
    isUserRegistered,
    status,
    usersCount,
    isRegistrationOpen,
    seatLimit,
  } = eventResponse;
  const userCount = isUserRegistered ? usersCount! - 1 : usersCount!;

  return (
    <ListCard
      disabled
      className={cn(
        'relative flex-shrink rounded bg-bg-primary px-24 lg:px-16 sm:px-16 xs:px-16',
        {
          'hover:bg-bg-secondary': isDesktop,
          'mb-48 xs:mb-16': isLastItem,
          'xs:pb-32': isLastItemInList,
        },
        className,
      )}
      content={event}
      direction="col">
      <Stack
        className={cn('event-list-item w-full border-divider py-24', {
          'border-b': !isLastItem,
          'h-full': Platform.OS === 'web',
        })}>
        <BaseCard.Image
          alt="event image"
          className={cn(
            'flex-shrink-0 rounded-8',
            imageBorderStyles({ size: '1px' }),
            listCardImageStyles,
            imageContainerStyles,
          )}
          containerClassName={cn(
            'xs:mt-4 sm:mt-4 lg:mt-4 flex-shrink-0 rounded-8 overflow-hidden',
            listCardImageStyles,
          )}
          containerStyle={{
            width: (imageProps?.width ?? 0) * 10,
            height: (imageProps?.height ?? 0) * 10,
          }}
          height={(imageProps?.height ?? 0) * 10}
          width={(imageProps?.width ?? 0) * 10}
        />
        <Stack
          alignItems="start"
          className="flex-1 truncate ms-24 xs:ms-16"
          direction="col"
          justifyContent="start">
          <Stack alignItems="center" className="mt-8 w-full xs:mt-0" gapX={8}>
            <BaseCard.Name
              className={cn('flex flex-shrink truncate text-center', {
                'whitespace-pre-line': isMobile,
              })}
              color="text-text-paragraph"
              numberOfLines={isMobile ? 2 : 1}
              variant={isMobile ? 'heading-xs' : 'heading-s'}
            />
            {props.event.recurringCategory === RecurringTypeEnum.RecurringInstance ? (
              <Tooltip title={t('Events:RecurringSession.RecurringSession')}>
                <RepeatOutline className="h-16 w-16 text-icon-placeholder hover:text-icon-secondary" />
              </Tooltip>
            ) : null}
          </Stack>
          <Typography
            className="mt-8 whitespace-pre-line xs:mt-6"
            color={isMobile ? 'text-text-secondary' : 'text-text-primary'}
            variant={isMobile ? 'body-short-01' : 'body-short-02'}>
            {event.date}
            <Typography as="span" className="text-text-primary xs:text-icon-secondary">
              &nbsp;•&nbsp;
            </Typography>
            {event.duration}
          </Typography>
          {event.status ? (
            <BaseCard.Status
              className="mt-12 self-start"
              size="medium"
              variant={status === EventStatusEnum.Completed ? 'light' : 'filled'}
            />
          ) : (
            <Stack className="mt-16 self-start space-s-32 xs:space-s-16" direction="row">
              <RegisteredClassInfo
                color={{ text: isMobile ? 'text-text-secondary' : undefined }}
                isRegistrationRequired={isRegistrationRequired}
                isUserRegistered={isUserRegistered}
                status={status}
                usersCount={userCount}
              />
              <SeatsLeft
                color={{
                  text: isMobile ? 'text-text-secondary' : undefined,
                  icon: isMobile ? 'icon-secondary' : undefined,
                }}
                isFillingFast={isFillingFast}
                isRegistrationOpen={isRegistrationOpen}
                isRegistrationRequired={isRegistrationRequired}
                isUserRegistered={isUserRegistered}
                seatLimit={seatLimit}
                status={status}
                usersCount={usersCount}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </ListCard>
  );
}

export default React.memo(EventListCard);
